"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Compass, Cpu, Wrench, TrendingUp, Rocket, ShieldCheck, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
interface CommitmentItem {
  id: string;
  icon: typeof Compass;
  title: {
    vi: string;
    en: string;
  };
  subtitle: {
    vi: string;
    en: string;
  };
}
const COMMITMENTS: CommitmentItem[] = [{
  id: "pathway",
  icon: Compass,
  title: {
    vi: "MINH BẠCH VỀ LỘ TRÌNH",
    en: "TRANSPARENT LEARNING PATHWAY"
  },
  subtitle: {
    vi: "Lộ trình đào tạo bài bản từ 4–16 tuổi, rõ ràng mục tiêu và chuẩn đầu ra từng cấp độ.",
    en: "Structured 4–16 learning pathway with clear goals and defined stage outcomes."
  }
}, {
  id: "equipment",
  icon: Cpu,
  title: {
    vi: "RÕ RÀNG VỀ THIẾT BỊ",
    en: "CLEAR ABOUT EQUIPMENT"
  },
  subtitle: {
    vi: "Sử dụng 100% bộ giáo cụ robotics & LEGO chuẩn quốc tế, công khai thông số kỹ thuật.",
    en: "100% international-standard LEGO & robotics kits with transparent equipment specs."
  }
}, {
  id: "practice",
  icon: Wrench,
  title: {
    vi: "HỌC TẬP QUA THỰC HÀNH",
    en: "LEARNING THROUGH PRACTICE"
  },
  subtitle: {
    vi: "80% thời lượng lớp học là lắp ráp & lập trình thực tế, sáng tạo sản phẩm hoạt động được.",
    en: "80% hands-on building & coding time to create functional engineering projects."
  }
}, {
  id: "progress",
  icon: TrendingUp,
  title: {
    vi: "TIẾN BỘ THEO TỪNG GIAI ĐOẠN",
    en: "PROGRESS STAGE BY STAGE"
  },
  subtitle: {
    vi: "Báo cáo nhận xét và đo lường sự phát triển tư duy của học viên sau từng chặng học.",
    en: "Detailed progress reports measuring skill growth and critical thinking after each module."
  }
}, {
  id: "growth",
  icon: Rocket,
  title: {
    vi: "CÓ NHIỀU HƯỚNG PHÁT TRIỂN",
    en: "MULTIPLE GROWTH PATHWAYS"
  },
  subtitle: {
    vi: "Định hướng thi đấu đấu trường quốc tế, làm đồ án nghiên cứu hoặc học chuyên sâu.",
    en: "Clear tracks for international robotics competitions, research projects, or advanced coding."
  }
}];
export default function ParentCommitments() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const savedLang = localStorage.getItem("roboto_lang") as "vi" | "en" | null;
    if (savedLang === "vi" || savedLang === "en") {
      setLang(savedLang);
    }
    const handleLangChange = (e: CustomEvent<{
      lang: "vi" | "en";
    }> | Event) => {
      const customEvent = e as CustomEvent<{
        lang: "vi" | "en";
      }>;
      if (customEvent.detail && customEvent.detail.lang) {
        setLang(customEvent.detail.lang);
      } else {
        const updated = localStorage.getItem("roboto_lang") as "vi" | "en" | null;
        if (updated === "vi" || updated === "en") {
          setLang(updated);
        }
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
    };
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="parentcommitments" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {/* Precision grid pattern overlay decoration */}
      <div className="absolute inset-0 pointer-events-none opacity-25 bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]" aria-hidden="true" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 lg:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/50 text-secondary-foreground bg-secondary/10 px-3 py-1 text-xs tracking-wider uppercase">
            <ShieldCheck className="size-3.5 text-secondary mr-1" />
            {lang === "vi" ? "ROBOTO ACADEMY TRUST" : "ROBOTO ACADEMY TRUST"}
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-serif">
            {lang === "vi" ? "CAM KẾT VỚI PHỤ HUYNH" : "OUR COMMITMENT TO PARENTS"}
          </h2>
          <div className="mt-4 h-1 w-16 bg-secondary mx-auto rounded-lg" />
        </div>

        {/* 2-Column Layout: Supporting Photo Showcase + 5-Card Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 max-w-6xl mx-auto items-stretch">
          {/* Supporting Photograph Frame */}
          <motion.div className="lg:col-span-5 flex flex-col justify-between" initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }}>
            <div className="relative h-full min-h-[380px] lg:min-h-[480px] rounded-xl overflow-hidden border border-border bg-card shadow-lg group flex flex-col justify-between p-2">
              <div className="relative w-full h-full min-h-[340px] rounded-lg overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/parent_student_interaction.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/parent_student_interaction.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/parent_student_interaction.png-wvc-sizes" alt={lang === "vi" ? "Tương tác giữa phụ huynh, thầy cô và học viên tại phòng lab ROBOTO" : "Parent-student classroom interaction in ROBOTO robotics academy lab"} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent" />

                {/* Technical badge overlay */}
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <Badge className="bg-primary text-primary-foreground text-xs font-mono uppercase tracking-wider border-none px-3 py-1 shadow-md">
                    <Users className="size-3.5 mr-1 inline-block" />
                    {lang === "vi" ? "ĐỒNG HÀNH LỚP HỌC" : "PARENTS & LAB INTERACTION"}
                  </Badge>
                </div>

                {/* Bottom spec caption card */}
                <div className="absolute bottom-4 left-4 right-4 p-4 rounded-lg bg-background/90 backdrop-blur-md border border-border/80 shadow-md">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="size-2 rounded-lg bg-secondary animate-pulse" />
                    <span className="text-xs font-mono font-semibold text-secondary-foreground uppercase tracking-widest">
                      {lang === "vi" ? "MÔI TRƯỜNG HỌC TẬP MINH BẠCH" : "TRANSPARENT LEARNING LAB"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {lang === "vi" ? "Phụ huynh dễ dàng theo dõi tiến bộ, trực tiếp trải nghiệm môi trường sáng tạo cùng con." : "Parents actively track progress and experience the creative technology ecosystem together."}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* 5-Card Trust Grid */}
          <motion.div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }}>
            {COMMITMENTS.map((item, index) => {
            const Icon = item.icon;
            const isLastCard = index === COMMITMENTS.length - 1;
            return <motion.div key={item.id} variants={itemVariants} className={isLastCard ? "sm:col-span-2" : ""} data-index={index}>
                  <Card className="h-full border border-border/80 bg-card hover:border-secondary hover:shadow-lg transition-[border-color,box-shadow,transform] duration-300 transform hover:-translate-y-1 group relative overflow-hidden">
                    {/* Spec rail accent line */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-muted group-hover:bg-secondary transition-colors duration-300" />

                    <CardHeader className="pt-6 pb-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="size-10 rounded-lg bg-muted flex items-center justify-center text-primary group-hover:bg-secondary/10 group-hover:text-secondary transition-colors duration-300">
                          <Icon className="size-5 stroke-[2]" />
                        </div>
                        <span className="text-xs font-mono text-muted-foreground/70">
                          0{index + 1}
                        </span>
                      </div>
                      <CardTitle className="text-base font-bold text-card-foreground leading-snug group-hover:text-primary transition-colors duration-200">
                        <h3 className="text-base font-semibold tracking-tight">
                          {item.title[lang]}
                        </h3>
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="pb-6">
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {item.subtitle[lang]}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </div>
    </section>;
}